/* eslint-disable jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { keyCode } from '@bingads-webui/keycode';
import { siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  IconButton,
  Link,
  DetailsList,
  DetailsRow,
  SelectionMode
} from '@fluentui/react';
import { DirectionalHint } from '@bingads-webui-react/fabric-control';
import { TeachingBubble } from '@bingads-webui-campaign-react/popup-components';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { withDefaultLandingPage } from '@bingads-webui-campaign-react/with-default-landing-page';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { getPathName, getPageItems, getPageItemUrl, getFlattenedItems, getGroupsForDetailList, mapToPageItems, getSingleItems, getTrimmedParams } from './common/page-item-helper';
import PageMenuItem from './components/page-menu-item';
import { getPageMenuStyles } from './styles/page-menu-detail-list-styles';

const columns = [
  { key: 'name', name: 'name', fieldName: 'name' },
];

function DetailListPageMenuInternal(props) {
  const {
    classes,
    siteMap,
    i18n,
    defaultLandingPage,
    setLandingPage,
    pageGroups,
    scope,
    noExpandGroups,
    permissions,
    serverContext,
  } = props;
  const [isTeachingBubbleVisible, setIsTeachingBubbleVisible] = useState(true);
  const campaignTypeQuery = props[WaterfallFilterType.CampaignType] && props[WaterfallFilterType.CampaignType].value && props[WaterfallFilterType.CampaignType].value.query;
  const campaignType = campaignTypeQuery && campaignTypeQuery.CampaignType && campaignTypeQuery.CampaignType.$in[0];
  const {
    RSAAutoGeneratedTeachingBubble,
  } = permissions && permissions.dynamic;
  const activity = useContext(ActivityContext);
  const pageItems = getPageItems(pageGroups, siteMap, i18n, defaultLandingPage, () => false);
  const items = mapToPageItems(getFlattenedItems(pageItems, noExpandGroups), i18n, defaultLandingPage, () => false);
  const groups = getGroupsForDetailList(pageItems, items, noExpandGroups);
  const singleItems = getSingleItems(groups, items);
  const iconExpandLabel = i18n.getString(_TL_('Expand'));
  const iconCollapseLabel = i18n.getString(_TL_('Collapse'));
  const onSelect = item => (event, isFromHeader) => {
    if (event.type !== 'click' && event.keyCode !== keyCode.Enter) {
      // allow only click & enter event to select the item
      return;
    }
    setClarityTag({ DetailListPageMenu: 'true' });
    event.preventDefault();
    if (!isFromHeader) {
      activity.signal(`details list - page menu item clicked, name: ${item.id}`);
    }
    if (item.isPageEnabled) {
      const trimmedParams = getTrimmedParams(item, scope);
      scope.set(trimmedParams);
      scope.save(getPathName(item));
    }
  };

  const onRenderItemColumn = (item) => {
    if (item.type === 'divider') {
      return null;
    }

    const singleItem = _.findWhere(singleItems, item);
    let renderItem = item;
    if (singleItem) {
      renderItem = _.findWhere(pageItems, { id: singleItem.parentId });
    }

    return (
      <div className={classes.pageMenuItem}>
        <PageMenuItem
          pageItem={renderItem}
          setLandingPage={setLandingPage}
          pageItemUrl={getPageItemUrl(renderItem, scope)}
          withRelationLine={!singleItem}
          showLabel
          fallbackTitle={renderItem.content}
        />
      </div>
    );
  };

  const onToggleCollapse = listprops => () => {
    activity.signal(`toggle icon clicked, ori: ${!!listprops.group.isCollapsed}`);
    listprops.onToggleCollapse(listprops.group);
  };

  const onGroupHeaderclick = listprops => (e) => {
    activity.signal(`group header clicked, name: ${listprops.group.keyId}`);
    if (listprops.group.isSelected) {
      listprops.onToggleCollapse(listprops.group);
    } else {
      onSelect(items[listprops.group.startIndex])(e, true);
    }
  };

  const onRenderGroupHeader = (listprops) => {
    if (listprops && listprops.group) {
      if (listprops.group.count > 1) {
        const { group } = listprops;
        const iconLabel = group.isCollapsed ? iconExpandLabel : iconCollapseLabel;
        const isSettingBannerExist = document.querySelector('.bingads-campaign-react-page-menu-item-settings') && (document.querySelector('.bingads-campaign-react-page-menu-item-settings').offsetHeight > 0);
        const MarketName = (serverContext && serverContext.HelpModel && serverContext.HelpModel.MarketName) || 'en-US';
        const language = (MarketName && MarketName.includes('-')) ? MarketName.split('-')[0] : 'en';

        return (
          <div
            className={classNames([classes.pageMenuGroupHeader, group.isSelected ? classes.pageMenuSelectedGroupHeader : '', group.isSelected ? 'selected' : '', 'bingads-campaign-react-page-menu-item', `bingads-campaign-react-page-menu-group-header-${group.keyId}`])}
            title={group.name}
          >
            <IconButton
              iconProps={{ iconName: group.isCollapsed ? 'ChevronRight' : 'ChevronDownMed' }}
              ariaLabel={iconLabel}
              onClick={onToggleCollapse(listprops)}
              className={classNames([
                classes.pageMenuGroupHeaderIcon,
                group.isCollapsed ? '' : classes.pageMenuGroupHeaderDownIcon,
                group.isCollapsed ? 'ChevronRight' : 'ChevronDownMed',
              ])}
              aria-expanded={!group.isCollapsed}
            />
            <Link
              role="button"
              onClick={onGroupHeaderclick(listprops)}
              className={classNames([`bingads-campaign-react-page-menu-item-${group.keyId}`, 'page-menu-item-content', classes.pageMenuGroupHeaderContent])}
              aria-expanded={!group.isCollapsed}
            >
              {group.name}
            </Link>
            {RSAAutoGeneratedTeachingBubble && campaignType && campaignType.includes('Default') && isTeachingBubbleVisible && isSettingBannerExist && group.keyId === 'settings' &&
            <TeachingBubble
              featureName="rsa-autogenerated-assets"
              title={i18n.getString(_TL_('Enable auto-generated assets for responsive search ads'))}
              content={i18n.getString(_TL_('Save time and maximize ad performance by enabling auto-generated assets for responsive search ads. We‘ll leverage content from your website’s landing page to customize your headlines and descriptions and make them more relevant.'))}
              onDone={() => { setIsTeachingBubbleVisible(false); window.open(`https://help.ads.microsoft.com/#apex/ads/${language}/help:60037/0/${MarketName}/#EnableAutoAssets`); }}
              onDismiss={() => { setIsTeachingBubbleVisible(false); }}
              onClose={() => { setIsTeachingBubbleVisible(false); }}
              primaryButtonText={i18n.getString(_TL_('Learn more'))}
              targetElement=".bingads-campaign-react-page-menu-item-settings"
              directionalHint={DirectionalHint.rightBottomEdge}
            />
            }
          </div>
        );
      }

      if (!listprops.group.name) { // divider
        return (
          <div
            key={listprops.group.key}
            className={classNames([classes.divider, 'bingads-campaign-react-page-menu-divider'])}
          />
        );
      }

      return <div className={classes.pageMenuSingleItemGroupHeader} />;
    }
    return null;
  };

  const onRenderRow = (rowProps) => {
    const singleItem = _.findWhere(singleItems, rowProps.item);
    let renderItem = rowProps.item;
    if (singleItem) {
      renderItem = _.findWhere(pageItems, { id: singleItem.parentId });
    }

    return (<DetailsRow
      {...rowProps}
      className={classNames([
        classes.pageMenuRow,
        'page-menu-detail-list-row',
      ])}
      onClick={onSelect(renderItem)}
      onKeyDown={onSelect(renderItem)}
      role="row"
    />);
  };

  const pageMenuClassName = classNames([
    classes.pageMenuContainer,
    classes.leftNavigationStyle,
    'bingads-campaign-react-page-menu',
  ]);

  return (
    <nav className={pageMenuClassName} aria-label={i18n.getString(_TL_('Page menu'))} >
      <div className="list-selector-content">
        <DetailsList
          items={items}
          groups={groups}
          groupProps={{
            onRenderHeader: onRenderGroupHeader,
          }}
          columns={columns}
          selectionMode={SelectionMode.none}
          isHeaderVisible={false}
          onRenderItemColumn={onRenderItemColumn}
          onRenderRow={onRenderRow}
          compact
          isPlaceholderData
          indentWidth={21}
          styles={{
            root: {
              backgroundColor: '#F3f2f1',
            },
            headerWrapper: {
              backgroundColor: '#F3f2f1',
            },
            contentWrapper: {
              backgroundColor: '#F3f2f1',
            },
          }}
          listProps={{
            className: classNames([
              classes.list,
              'page-menu-detail-list-list',
            ]),
          }}
          role="treegrid"
        />
      </div>
    </nav>
  );
}

DetailListPageMenuInternal.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getPageMenuStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  siteMap: siteMapPropType.isRequired,
  defaultLandingPage: PropTypes.string,
  setLandingPage: PropTypes.func,
  perfMarker: PropTypes.shape({
    done: PropTypes.func.isRequired,
    isDone: PropTypes.bool.isRequired,
  }),
  pageGroups: PropTypes.arrayOf(PropTypes.array).isRequired,
  noExpandGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
};

DetailListPageMenuInternal.defaultProps = {
  defaultLandingPage: null,
  setLandingPage: null,
  perfMarker: null,
};

export const DetailListPageMenu = withDefaultLandingPage(withI18nConsumer(withDefaultStyles(
  DetailListPageMenuInternal,
  getPageMenuStyles
)));
